import type { NonfictionProgressFragment, NonfictionQuizAttemptFragment } from "./generated";
import { api } from "./generated";

/**
 * AIDEV-NOTE: Zrcadlo practiceQuizAttemptApi — mutace neuměleckých textů vracejí celý nový
 * stav progressu, odpověď se zapisuje rovnou do cache getNonfictionProgress místo
 * invalidace tagů (druhý request po každém otočení kartičky). Dotaz nemá argumenty
 * (jeden progress na studenta), proto `undefined` místo `{ bookId }`. Hooky featury se
 * importují odsud, ne z api/generated, aby enhancement vždy proběhl před prvním
 * použitím. [TD-346]
 */
const upsertProgress = (progress: NonfictionProgressFragment) =>
  nonfictionProgressApi.util.updateQueryData("getNonfictionProgress", undefined, draft => {
    draft.nonfictionProgress = progress;
  });

/**
 * AIDEV-NOTE: Pokus kvízu je od TD-352 samostatný dokument a druhé kořenové pole TÉHOŽ
 * dotazu getNonfictionProgress — patch míří do stejné cache entry jako upsertProgress,
 * jen na jinou větev. [TD-352]
 */
const upsertAttempt = (attempt: NonfictionQuizAttemptFragment) =>
  nonfictionProgressApi.util.updateQueryData("getNonfictionProgress", undefined, draft => {
    draft.nonfictionQuizAttempt = attempt;
  });

export const nonfictionProgressApi = api.enhanceEndpoints({
  endpoints: {
    flipNonfictionCard: {
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertProgress(data.nonfictionCardFlip));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    setNonfictionProgress: {
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertProgress(data.nonfictionProgressSet));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    startNonfictionQuizAttempt: {
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertAttempt(data.nonfictionQuizAttemptStart));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    rateNonfictionQuizQuestion: {
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertAttempt(data.nonfictionQuizQuestionRate.attempt));

          // `progress` přijde JEN když uzavírající verdikt změnil hasConsolidated.
          // Bezpodmínečný patch by u ostatních verdiktů přepsal načtený progress
          // na null a tečka nad tabem by zhasla. [TD-352]
          const { progress } = data.nonfictionQuizQuestionRate;
          if (progress != null) dispatch(upsertProgress(progress));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
  },
});

export const {
  useGetNonfictionProgressQuery,
  useFlipNonfictionCardMutation,
  useSetNonfictionProgressMutation,
  useStartNonfictionQuizAttemptMutation,
  useRateNonfictionQuizQuestionMutation,
} = nonfictionProgressApi;
